/**
 * Subscription System - Type-Safe Helper Functions
 *
 * Utility functions for checking features, limits, and subscription status
 */

import { PLAN_CODES, SUBSCRIPTION_STATUS } from './subscription'
import type {
  PlanCode,
  PlanComparison,
  PlanDocument,
  SubscriptionDocument,
  UsageDocument,
  UsageLimitCheckResult,
  UsageLimitKey,
} from './subscription'

// ============================================================================
// PLAN TIER LEVELS
// ============================================================================

/**
 * Plan tier levels for comparison. Lower index = lower tier.
 * Legacy Team AI shares Team's level: switching between them is neither an
 * upgrade nor a downgrade.
 */
export const PLAN_TIER_LEVELS = {
  [PLAN_CODES.FREE]: 0,
  [PLAN_CODES.INDIVIDUAL]: 1,
  [PLAN_CODES.TEAM]: 2,
  [PLAN_CODES.AI]: 2,
} as const

/**
 * The purchasable tiers in upgrade order. Next/previous-tier navigation walks
 * this list rather than PLAN_TIER_LEVELS, because two codes can share a level.
 */
const PUBLIC_TIER_ORDER: ReadonlyArray<PlanCode> = [
  PLAN_CODES.FREE,
  PLAN_CODES.INDIVIDUAL,
  PLAN_CODES.TEAM,
]

/** Position of a plan in PUBLIC_TIER_ORDER; legacy Team AI counts as Team. */
function publicTierIndex(plan: PlanCode): number {
  return PUBLIC_TIER_ORDER.indexOf(plan === PLAN_CODES.AI ? PLAN_CODES.TEAM : plan)
}

// ============================================================================
// USAGE LIMIT CHECKS
// ============================================================================

/**
 * Check usage against plan limits
 *
 * @param plan - The plan document
 * @param usage - Current usage metrics
 * @param limit - The limit key to check
 * @returns Detailed usage information
 *
 * @example
 * ```typescript
 * const result = checkUsageLimit(plan, usage, USAGE_LIMITS.TASKS)
 * if (result.isOverLimit) {
 *   showUpgradePrompt(`You've used ${result.percentUsed}% of your tasks`)
 * } else if (result.isNearingLimit) {
 *   showWarning(`Only ${result.remaining} tasks remaining`)
 * }
 * ```
 */
export function checkUsageLimit(
  plan: PlanDocument | null,
  usage: UsageDocument | null,
  limit: UsageLimitKey,
): UsageLimitCheckResult {
  if (!plan) {
    return {
      isOverLimit: true,
      currentUsage: 0,
      limit: 0,
      remaining: 0,
      percentUsed: 0,
      isUnlimited: false,
      canProceed: false,
    }
  }

  // Absent key = unlimited, matching the backend QuotaKeys convention (only the
  // optional AI trial caps can be absent).
  const planLimit = plan.limits[limit] ?? -1
  const currentUsage = usage?.usage[limit] ?? 0

  // -1 means unlimited
  if (planLimit === -1) {
    return {
      isOverLimit: false,
      currentUsage,
      limit: -1,
      remaining: Infinity,
      percentUsed: 0,
      isUnlimited: true,
      canProceed: true,
    }
  }

  const remaining = Math.max(0, planLimit - currentUsage)
  const percentUsed = planLimit > 0 ? (currentUsage / planLimit) * 100 : 0
  const isOverLimit = currentUsage >= planLimit
  const isNearingLimit = percentUsed >= 80 && !isOverLimit

  return {
    isOverLimit,
    currentUsage,
    limit: planLimit,
    remaining,
    percentUsed,
    isUnlimited: false,
    canProceed: !isOverLimit,
    warningThreshold: 80,
    isNearingLimit,
  }
}

/**
 * Simple limit check - returns boolean
 *
 * @param plan - The plan document
 * @param usage - Current usage
 * @param limit - The limit to check
 * @returns True if over the limit
 */
export function isOverLimit(
  plan: PlanDocument | null,
  usage: UsageDocument | null,
  limit: UsageLimitKey,
): boolean {
  return checkUsageLimit(plan, usage, limit).isOverLimit
}

/**
 * Get remaining quota for a limit
 *
 * @param plan - The plan document
 * @param usage - Current usage
 * @param limit - The limit to check
 * @returns Remaining quota (Infinity if unlimited)
 */
export function getRemainingQuota(
  plan: PlanDocument | null,
  usage: UsageDocument | null,
  limit: UsageLimitKey,
): number {
  const result = checkUsageLimit(plan, usage, limit)
  return result.remaining
}

// ============================================================================
// SUBSCRIPTION STATUS CHECKS
// ============================================================================

/**
 * Check if subscription is active and valid
 *
 * @param subscription - The subscription document
 * @returns True if subscription is active or trialing
 */
export function isSubscriptionActive(
  subscription: SubscriptionDocument | null,
): boolean {
  if (!subscription) return false
  const activeStatuses = [SUBSCRIPTION_STATUS.TRIALING, SUBSCRIPTION_STATUS.ACTIVE]
  return activeStatuses.includes(subscription.status as typeof activeStatuses[number])
}

/**
 * Check if subscription is in trial period
 */
export function isTrialing(
  subscription: SubscriptionDocument | null,
): boolean {
  return subscription?.status === SUBSCRIPTION_STATUS.TRIALING
}

/**
 * Check if subscription is expired
 */
export function isExpired(
  subscription: SubscriptionDocument | null,
): boolean {
  if (!subscription) return false
  return subscription.status === SUBSCRIPTION_STATUS.EXPIRED
}

/**
 * Check if subscription is past due
 */
export function isPastDue(
  subscription: SubscriptionDocument | null,
): boolean {
  return subscription?.status === SUBSCRIPTION_STATUS.PAST_DUE
}

/**
 * Check if subscription is canceled
 */
export function isCanceled(
  subscription: SubscriptionDocument | null,
): boolean {
  return subscription?.status === SUBSCRIPTION_STATUS.CANCELED
}

/**
 * Calculate days remaining in trial
 *
 * @param subscription - The subscription document
 * @returns Number of days left in trial (0 if not trialing)
 */
export function getTrialDaysLeft(
  subscription: SubscriptionDocument | null,
): number {
  if (
    !subscription ||
    !isTrialing(subscription) ||
    !subscription.trialEndDate
  ) {
    return 0
  }

  const now = Date.now()
  const msLeft = subscription.trialEndDate - now
  const daysLeft = Math.ceil(msLeft / (1000 * 60 * 60 * 24))

  return Math.max(0, daysLeft)
}

/**
 * Calculate days remaining in current billing period
 */
export function getPeriodDaysLeft(
  subscription: SubscriptionDocument | null,
): number {
  if (!subscription) return 0

  const now = Date.now()
  const msLeft = subscription.currentPeriodEnd - now
  const daysLeft = Math.ceil(msLeft / (1000 * 60 * 60 * 24))

  return Math.max(0, daysLeft)
}

// ============================================================================
// PLAN COMPARISON
// ============================================================================

/**
 * Compare two plans by tier level
 *
 * @param planA - First plan code
 * @param planB - Second plan code
 * @returns 'lower' | 'same' | 'higher'
 */
export function comparePlans(
  planA: PlanCode,
  planB: PlanCode,
): PlanComparison {
  const tierA = PLAN_TIER_LEVELS[planA]
  const tierB = PLAN_TIER_LEVELS[planB]

  if (tierA < tierB) return 'lower'
  if (tierA > tierB) return 'higher'
  return 'same'
}

// Highest tier index in PLAN_TIER_LEVELS. Updated when adding new tiers.
const HIGHEST_TIER_LEVEL = 2

/**
 * Check if plan can be upgraded
 */
export function canUpgrade(currentPlan: PlanCode | null): boolean {
  if (!currentPlan) return true
  return PLAN_TIER_LEVELS[currentPlan] < HIGHEST_TIER_LEVEL
}

/**
 * Check if plan can be downgraded
 */
export function canDowngrade(currentPlan: PlanCode | null): boolean {
  if (!currentPlan) return false
  return PLAN_TIER_LEVELS[currentPlan] > 0 // Not already on lowest tier
}

/**
 * Get next higher plan tier
 */
export function getNextTier(currentPlan: PlanCode): PlanCode | null {
  return PUBLIC_TIER_ORDER[publicTierIndex(currentPlan) + 1] ?? null
}

/**
 * Get next lower plan tier
 */
export function getPreviousTier(currentPlan: PlanCode): PlanCode | null {
  const index = publicTierIndex(currentPlan)
  return index > 0 ? (PUBLIC_TIER_ORDER[index - 1] ?? null) : null
}

// ============================================================================
// FORMATTING UTILITIES
// ============================================================================

/**
 * Format price from cents to dollars
 */
export function formatPrice(cents: number, currency: string = 'USD'): string {
  const dollars = cents / 100
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(dollars)
}

/**
 * Format bytes to human-readable string
 */
export function formatBytes(bytes: number): string {
  if (bytes === 0) return '0 B'
  if (bytes === -1) return 'Unlimited'

  const k = 1024
  const sizes = ['B', 'KB', 'MB', 'GB', 'TB']
  const i = Math.floor(Math.log(bytes) / Math.log(k))

  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`
}

/**
 * Format number to human-readable string
 */
export function formatNumber(num: number): string {
  if (num === -1) return 'Unlimited'
  if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`
  if (num >= 1000) return `${(num / 1000).toFixed(1)}K`
  return num.toString()
}

/**
 * Get user-friendly status label
 */
export function getStatusLabel(status: string): string {
  const labels: Record<string, string> = {
    trialing: 'Trial',
    active: 'Active',
    past_due: 'Past Due',
    canceled: 'Canceled',
    expired: 'Expired',
  }
  return labels[status] || status
}

/**
 * Get plan display name
 */
export function getPlanDisplayName(planCode: PlanCode): string {
  const names: Record<PlanCode, string> = {
    [PLAN_CODES.FREE]: 'Free',
    [PLAN_CODES.INDIVIDUAL]: 'Individual',
    [PLAN_CODES.TEAM]: 'Team',
    [PLAN_CODES.AI]: 'Team AI (legacy)',
  }
  return names[planCode]
}

/**
 * Get plan short name
 */
export function getPlanShortName(planCode: PlanCode): string {
  const names: Record<PlanCode, string> = {
    [PLAN_CODES.FREE]: 'Free',
    [PLAN_CODES.INDIVIDUAL]: 'Individual',
    [PLAN_CODES.TEAM]: 'Team',
    [PLAN_CODES.AI]: 'Team AI',
  }
  return names[planCode]
}
